import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets-jai6qajn.emergentagent.net/job_mortgage-elite/artifacts/actne5cd_StraviaLending_Caitlin_069.jpg";

const LINES = [
  { id: "line-1", parts: [{ text: "Elevated finance," }] },
  {
    id: "line-2",
    parts: [{ text: "arranged", italic: true }, { text: " with" }],
  },
  {
    id: "line-3",
    parts: [{ text: "quiet " }, { text: "care.", italic: true }],
  },
];

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.45 + i * 0.14 },
  }),
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-32 pb-24 overflow-hidden"
    >
      <div
        aria-hidden="true"
        data-testid="hero-plaid-band"
        className="hidden lg:block pointer-events-none absolute right-0 top-0 h-full w-14 plaid opacity-90 border-l border-black/[0.06]"
      />
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[46rem] h-[46rem] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(219,184,156,0.18) 0%, transparent 65%)" }}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full grid grid-cols-1 md:grid-cols-12 gap-16 items-center">
        <motion.div style={{ y: textY }} className="md:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.25 }}
            data-testid="hero-overline"
            className="text-xs font-semibold tracking-[0.35em] uppercase text-[#CFAEA5] mb-10"
          >
            Mont Elk Lending — Mobile Mortgage Broker, Australia Wide
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="font-display font-medium tracking-tight leading-[0.95] text-[14vw] sm:text-[10.5vw] lg:text-[7rem] text-balance"
          >
            {LINES.map((line, i) => (
              <span key={line.id} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {line.parts.map((p) =>
                    p.italic ? (
                      <em key={p.text} className="italic text-[#CFAEA5]">
                        {p.text}
                      </em>
                    ) : (
                      <span key={p.text}>{p.text}</span>
                    )
                  )}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 1.15 }}
            data-testid="hero-subtext"
            className="mt-10 max-w-md text-base leading-relaxed tracking-wide text-[#908B85]"
          >
            Bespoke lending for the homes and portfolios that matter most —
            guided personally by Caitlin Morrison, from first conversation to
            settlement day.
          </motion.p>
          <motion.a
            href="#philosophy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.5 }}
            data-testid="hero-scroll-cue"
            className="mt-16 inline-flex items-center gap-4 text-xs tracking-[0.3em] uppercase text-[#908B85] hover:text-[#493E38] transition-colors duration-300"
          >
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown size={16} />
            </motion.span>
            Scroll to explore
          </motion.a>
        </motion.div>
        <div className="md:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
            className="relative overflow-hidden rounded-t-[999px] rounded-b-[4px] aspect-[3/4] shadow-[0_4px_40px_-10px_rgba(0,0,0,0.12)]"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={HERO_IMAGE}
              alt="Caitlin Morrison in conversation with a client"
              data-testid="hero-image"
              style={{ y: imageY, scale: 1.2 }}
              className="w-full h-full object-cover"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-6 text-[11px] tracking-[0.3em] uppercase text-[#908B85] text-right"
            data-testid="hero-image-caption"
          >
            Caitlin Morrison — Founder &amp; Broker
          </motion.p>
        </div>
      </div>
    </section>
  );
};
